import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layouts/DashboardLayout';
import DashboardOverviewWA from './website-admin/DashboardOverview';
import TestimonialApproval from './website-admin/TestimonialApproval';
import { formComponentMap } from './website-admin/forms';
import ApprovalStatusHistory from './website-admin/ApprovalStatusHistory';
import { Wrench } from 'lucide-react';
import '../styles/dashboard.css';
import './website-admin/WebsiteAdminDashboard.css';

function WebsiteAdminDashboard({ user, onLogout, apiBase }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    totalContent: 38,
    draftContent: 0,
    pendingApproval: 0,
    publishedContent: 38,
    pendingTestimonials: 0,
    totalTestimonials: 0,
    approvedTestimonials: 0
  });

  const loadStats = useCallback(async () => {
    try {
      let totalC = 38;
      let pubC = 38;
      let draftC = 0;
      let pendC = 0;

      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const authHeaders = { 'Content-Type': 'application/json' };
      if (savedToken) {
        authHeaders['Authorization'] = `Bearer ${savedToken}`;
      }

      // 1. Fetch contents
      try {
        const response = await fetch(`${apiBase}/contents?limit=100`, {
          credentials: 'include',
          headers: authHeaders,
        });
        if (response.ok) {
          const data = await response.json();
          if (data.ok && data.data) {
            const items = data.data.contents || [];
            if (items.length > 0) {
              totalC = items.length;
              pubC = items.filter(c => c.status_id === 3 || c.status === 'published').length || totalC;
              draftC = items.filter(c => c.status_id === 1 || c.status === 'draft').length;
              pendC = items.filter(c => c.status_id === 2 || c.status === 'pending').length;
            } else if (data.data.pagination) {
              totalC = data.data.pagination.total || 38;
            }
          }
        }
      } catch (cErr) {
        console.warn('[WebsiteAdminDashboard] Error loading contents:', cErr);
      }

      // 2. Fetch pending Principal approvals
      try {
        const faRes = await fetch(`${apiBase}/homepage/cms/field-approvals?status=pending&limit=200&_t=${Date.now()}`, {
          credentials: 'include',
          headers: authHeaders
        });
        if (faRes.ok) {
          const faData = await faRes.json();
          if (faData.status_counts && typeof faData.status_counts.pending === 'number') {
            pendC = faData.status_counts.pending;
          } else if (Array.isArray(faData.data)) {
            pendC = faData.data.length;
          }
        } else {
          const pRes = await fetch(`${apiBase}/homepage/cms/pending-approvals?limit=200`, {
            credentials: 'include',
            headers: authHeaders
          });
          if (pRes.ok) {
            const pData = await pRes.json();
            const pItems = pData.data?.items || [];
            pendC = pData.data?.pagination?.total ?? pItems.length;
          }
        }
      } catch (pErr) {
        console.warn('[WebsiteAdminDashboard] Error loading approvals:', pErr);
      }

      // 3. Fetch testimonials
      let pendingT = 0;
      let totalT = 0;
      let approvedT = 0;
      try {
        const tRes = await fetch(`${apiBase}/testimonials?limit=100`, {
          credentials: 'include',
          headers: authHeaders,
        });
        if (tRes.ok) {
          const tData = await tRes.json();
          const tList = tData.data?.testimonials || [];
          totalT = tData.data?.pagination?.total || tList.length;
          pendingT = tList.filter(t => t.status === 'pending').length;
          approvedT = tList.filter(t => t.status === 'approved').length;
        }
      } catch (tErr) {
        console.warn('[WebsiteAdminDashboard] Error loading testimonials:', tErr);
      }

      setStats({
        totalContent: totalC,
        publishedContent: pubC,
        draftContent: draftC,
        pendingApproval: pendC,
        pendingTestimonials: pendingT,
        totalTestimonials: totalT,
        approvedTestimonials: approvedT
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, [apiBase]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleTabChange = (tabId) => {
    console.log('[WebsiteAdminDashboard] Tab changed to:', tabId);
    setActiveTab(tabId);
  };

  // Render content based on active tab
  const renderContent = () => {
    if (activeTab === 'dashboard') {
      return (
        <DashboardOverviewWA 
          apiBase={apiBase} 
          stats={stats} 
          onRefresh={loadStats} 
          onNavigateTab={handleTabChange}
          user={user}
        />
      );
    }

    if (activeTab === 'testimonials-management' || activeTab === 'testimonials-review' || activeTab === 'testimonials-admin') {
      return <TestimonialApproval apiBase={apiBase} />;
    }

    if (activeTab === 'approval-status' || activeTab === 'approval-history' || activeTab === 'submissions') {
      return (
        <ApprovalStatusHistory 
          apiBase={apiBase} 
          user={user} 
          onNavigateTab={handleTabChange} 
          onRefreshStats={loadStats} 
        />
      );
    }

    // Direct lookup from formComponentMap
    const FormComponent = formComponentMap[activeTab];
    
    if (FormComponent) {
      return <FormComponent apiBase={apiBase} activeTab={activeTab} />;
    }

    // Default: Coming Soon (only if no form exists)
    return (
      <div className="dashboard-content">
        <div style={{
          maxWidth: '600px',
          margin: '60px auto',
          textAlign: 'center',
          padding: '40px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            <Wrench size={48} color="#2563eb" />
          </div>
          <h2 style={{ color: '#333', marginBottom: '12px' }}>
            {activeTab.replace(/cms-|_/g, ' ').replace(/-/g, ' ').toUpperCase()}
          </h2>
          <p style={{ color: '#666', fontSize: '16px' }}>
            Form editor coming soon
          </p>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout
      user={user}
      onLogout={onLogout}
      onTabChange={handleTabChange}
      userRole="website_admin"
      apiBase={apiBase}
      activeTab={activeTab}
      pendingCount={stats?.pendingApproval || 0}
    >
      {renderContent()}
    </DashboardLayout>
  );
}

export default WebsiteAdminDashboard;
